'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { useCallback, useMemo } from 'react';
import { CheckboxWithLabel } from 'ui-components';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import {
  SALE_LABEL_FALLBACKS,
  SALE_MICROCOPY_MAP,
  getSaleController,
} from '../facetControllers';
import { useOptimisticFacetSelection } from '../hooks/useOptimisticFacetSelection';
import { useStableFacetValues } from '../hooks/useStableFacetValues';
import { SaleFacetProps } from './SaleFacet.types';

export const SaleFacet = ({
  variant = 'sidebar',
  className,
  isOpen,
  onToggle,
}: SaleFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const saleTitle =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.sale.title')?.value ??
    'Sale';

  const eng = useEngine();
  const controller = useMemo(() => getSaleController(eng), [eng]);
  const state = useController(controller);
  const stableValues = useStableFacetValues(
    state.values,
    'ec_sale_filters',
    eng
  );
  const { optimisticValues, selectedCount, findStableValue, applyOptimistic } =
    useOptimisticFacetSelection(stableValues);

  const getLabel = (value: string) => {
    const microcopyKey = SALE_MICROCOPY_MAP[value];
    if (microcopyKey) {
      const entry = searchLabelsMicrocopy?.find((m) => m.key === microcopyKey);
      if (entry?.value) return entry.value;
    }
    return SALE_LABEL_FALLBACKS[value] ?? value;
  };

  const handleClear = useCallback(() => {
    controller.deselectAll();
  }, [controller]);

  if (stableValues.length === 0) {
    return null;
  }

  const isDark = variant === 'mobile';

  const content = (
    <div className="flex flex-col">
      {optimisticValues.map((facetValue) => (
        <CheckboxWithLabel
          key={`sale-${facetValue.value}`}
          id={`sale-${facetValue.value}`}
          checked={facetValue.state === 'selected'}
          disabled={
            facetValue.numberOfResults === 0 && facetValue.state !== 'selected'
          }
          onCheckedChange={() => {
            const stableValue = findStableValue(facetValue);
            if (!stableValue) return;
            applyOptimistic([stableValue], () =>
              controller.toggleSelect(stableValue)
            );
            controller.toggleSelect(stableValue);
          }}
          count={facetValue.numberOfResults}
          isDarkTheme={isDark}
          microcopy={{ label: getLabel(facetValue.value) }}
        />
      ))}
    </div>
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={saleTitle}
        selectedCount={selectedCount}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={saleTitle}
      variant={variant}
      className={className}
      onClear={handleClear}
      showClear={variant === 'drawer' && selectedCount > 0}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
